
import { defineComponent, PropType } from 'vue'

import KnowledgeCombobox from '@/components/Knowledge/KnowledgeCombobox.vue'
import {
  DEFAULT_KNOWLEDGE_FILTERS,
  KnowledgeFilterField,
  KnowledgeFilterOption,
  KnowledgeFilters
} from '~/utils/knowledge/types'
import { checkArrayEquality } from '~/utils/array'

export default defineComponent({
  name: 'KnowledgeFilters',
  components: {
    KnowledgeCombobox
  },
  props: {
    value: {
      type: Object as PropType<KnowledgeFilters>,
      default: () => ({ ...DEFAULT_KNOWLEDGE_FILTERS })
    },
    fields: {
      type: Array as PropType<Array<KnowledgeFilterField>>,
      required: true
    },
  },
  data() {
    const searchKey = this.fields.find((f) => f.kind === 'search')?.key
    return {
      local: { ...this.value } as KnowledgeFilters,
      searchText: (searchKey && typeof this.value[searchKey] === 'string'
        ? (this.value[searchKey] as string)
        : '') as string
    }
  },
  computed: {
    searchField(): KnowledgeFilterField | undefined {
      return this.fields.find((f) => f.kind === 'search')
    },
    menuFields(): Array<KnowledgeFilterField> {
      return this.fields.filter((f) => f.kind !== 'search')
    }
  },
  watch: {
    value: {
      handler(next: KnowledgeFilters): void {
        // Skip the echo of our own change; only sync on a genuine external update.
        if (
          next.content === this.local.content &&
          next.source === this.local.source &&
          checkArrayEquality(next.category, this.local.category)
        )
          return
        this.local = { ...next }
        if (this.searchField) {
          const incoming = next[this.searchField.key]
          this.searchText = typeof incoming === 'string' ? incoming : ''
        }
      },
      deep: true
    }
  },
  methods: {
    emitChange(): void {
      this.$emit('change', { ...this.local })
    },
    isChecked(field: KnowledgeFilterField, value: string): boolean {
      const v = this.local[field.key]
      return Array.isArray(v) && v.includes(value as never)
    },
    multiCount(field: KnowledgeFilterField): number {
      const v = this.local[field.key]
      return Array.isArray(v) ? v.length : 0
    },
    isFieldActive(field: KnowledgeFilterField): boolean {
      const v = this.local[field.key]
      return Array.isArray(v) ? v.length > 0 : Boolean(v)
    },
    triggerClasses(field: KnowledgeFilterField, isOpen: boolean): string {
      if (this.isFieldActive(field)) return 'bg-ink-200 hover:bg-ink-100'
      if (isOpen) return 'border-dashed bg-ink-200 text-vanilla-100'
      return 'border-dashed text-vanilla-400 hover:text-vanilla-100'
    },
    onSearchInput(field: KnowledgeFilterField, raw: string): void {
      this.searchText = raw
      const trimmed = raw.trim()
      this.local = { ...this.local, [field.key]: trimmed === '' ? null : trimmed }
      this.emitChange()
    },
    onSingleSelect(field: KnowledgeFilterField, value: string, close: () => void): void {
      this.local = { ...this.local, [field.key]: value }
      this.emitChange()
      close()
    },
    onSingleReset(field: KnowledgeFilterField): void {
      this.local = { ...this.local, [field.key]: null }
      this.emitChange()
    },
    comboboxValue(field: KnowledgeFilterField): KnowledgeFilterOption | null {
      const v = this.local[field.key]
      return v && typeof v === 'object' ? (v as KnowledgeFilterOption) : null
    },
    onComboboxChange(field: KnowledgeFilterField, value: KnowledgeFilterOption | null): void {
      this.local = { ...this.local, [field.key]: value }
      this.emitChange()
    },
    onMultiToggle(field: KnowledgeFilterField, value: string): void {
      const current = this.local[field.key]
      const list = Array.isArray(current) ? [...current] : []
      const idx = list.indexOf(value as never)
      if (idx === -1) {
        list.push(value as never)
      } else {
        list.splice(idx, 1)
      }
      this.local = { ...this.local, [field.key]: list }
      this.emitChange()
    }
  }
})
